import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import { Button, LinearProgress, MenuItem, TextField } from "@material-ui/core";
import VideocamIcon from "@material-ui/icons/Videocam";

import { createDraftVideo } from "../../Redux/Actions/agent";
import { Avatar, Draft } from "../../Redux/Sagas/agent/api";
import { isVideoBusy } from "./status";

interface IProps {
  draft: Draft;
  avatars: Avatar[];
  pendingAction?: string;
}

// Video reply for one draft: pick an avatar, generate, watch progress, play.
const VideoPanel: React.FC<IProps> = ({ draft, avatars, pendingAction }) => {
  const dispatch = useDispatch();
  const [avatarId, setAvatarId] = useState("");
  const video: any = draft.video || { status: "none" };
  const readyAvatars = avatars.filter((avatar) => avatar.status === "ready");
  const defaultAvatar = readyAvatars.find((avatar) => avatar.isDefault) || readyAvatars[0];
  const selectedAvatarId =
    readyAvatars.some((avatar) => avatar.id === avatarId) ? avatarId : defaultAvatar?.id || "";

  const busy = isVideoBusy(draft);
  const locked = draft.status === "sent" || draft.status === "sending";
  const requesting = Boolean(pendingAction);
  if (locked && video.status !== "ready") return null;

  return (
    <div className="inboxSection">
      <div className="inboxSectionTitle">Video reply</div>

      {busy && (
        <div style={{ marginBottom: 10 }}>
          <LinearProgress />
          <div className="aiMuted" style={{ marginTop: 6 }}>
            {video.status === "queued" ? "Video queued" : "Rendering video"}
            {video.progress ? ` · ${video.progress}` : ""}. This usually takes a few minutes; the
            page updates on its own.
          </div>
        </div>
      )}

      {video.status === "ready" && video.playbackUrl && (
        <>
          <video
            className="inboxVideo"
            controls
            preload="metadata"
            src={video.playbackUrl}
            poster={video.thumbnailUrl || undefined}
          />
          <div className="aiActions" style={{ marginBottom: 10 }}>
            {video.watchUrl && (
              <a href={video.watchUrl} target="_blank" rel="noopener noreferrer">
                Open watch page
              </a>
            )}
          </div>
          {video.stale && !locked && (
            <div className="aiNotice aiNotice--warn">
              The text changed since this video was made, so it will not be included in the email.
              Regenerate it so the video matches?
            </div>
          )}
          {!video.stale && !locked && (
            <div className="aiMuted" style={{ marginBottom: 10 }}>
              When you send this reply, the email includes the video thumbnail and a link to watch
              it.
            </div>
          )}
        </>
      )}

      {video.status === "failed" && (
        <div className="aiNotice aiNotice--error">
          The video could not be made{video.error ? `: ${video.error}` : "."}
        </div>
      )}

      {!busy && !locked && (
        <>
          {readyAvatars.length === 0 ? (
            <div className="aiMuted">
              You need a ready avatar to make a video. <Link to="/avatars">Add an avatar</Link>
            </div>
          ) : (
            <div className="aiActions">
              <TextField
                select
                size="small"
                variant="outlined"
                label="Avatar"
                value={selectedAvatarId}
                onChange={(event) => setAvatarId(event.target.value)}
                className="inboxAvatarSelect"
              >
                {readyAvatars.map((avatar) => (
                  <MenuItem key={avatar.id} value={avatar.id}>
                    {avatar.name}
                    {avatar.isDefault ? " (default)" : ""}
                  </MenuItem>
                ))}
              </TextField>
              <Button
                variant="outlined"
                startIcon={<VideocamIcon />}
                disabled={requesting || !selectedAvatarId}
                onClick={() => dispatch(createDraftVideo(draft.id, selectedAvatarId))}
              >
                {requesting
                  ? "Starting..."
                  : video.status === "ready" || video.status === "failed"
                  ? "Regenerate video"
                  : "Generate video"}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default VideoPanel;
